import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar, faStarHalfAlt, faUser, faCalendarAlt,
  faComment, faPlus, faTrash, faEdit,
  faTimes, faCheck, faExclamationTriangle,
  faSyncAlt, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { Modal, Form, Button } from 'react-bootstrap';
import Dexie from 'dexie';

// ============================================================
// CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('EmployeeEvaluationCacheDB');
db.version(1).stores({ evaluations: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedEvaluations(key, allowExpired = false) {
  try {
    const entry = await db.evaluations.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedEvaluations(key, data) {
  await db.evaluations.put({ key, data, timestamp: Date.now() });
}
async function getStaleEvaluations(key) {
  return getCachedEvaluations(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}employe/api_evaluation2.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const EmployeeEvaluationWidget = ({ employeId, onEvaluationAdded, onEvaluationDeleted }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('cache');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState(null);
  const [evaluateurs, setEvaluateurs] = useState([]);
  const [formData, setFormData] = useState({
    evaluateur_id: '',
    date_evaluation: '',
    commentaire: '',
    scores: {},
    note_globale: ''
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // RÉCUPÉRATION DES ÉVALUATIONS (avec cache, dédoublonnage, annulation)
  // ============================================================
  const fetchEvaluations = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (!employeId) return;

    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `evaluations_${employeId}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setEvaluations(result);
          setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
          setError(null);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        setError('Erreur : trop de tentatives. Veuillez réessayer plus tard.');
      }
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedEvaluations(cacheKey);
          if (cached && isMounted.current) {
            setEvaluations(cached);
            setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&employe_id=${employeId}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleEvaluations(cacheKey);
          if (stale && isMounted.current) {
            setEvaluations(stale);
            setSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchEvaluations(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleEvaluations(cacheKey);
          if (stale && isMounted.current) {
            setEvaluations(stale);
            setSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const results = data.data || [];
          if (isMounted.current) {
            setEvaluations(results);
            const sourceLabel = forceRefresh ? 'live (forcé)' : 'live';
            setSource(sourceLabel);
            setLoading(false);
            setRefreshing(false);
            await setCachedEvaluations(cacheKey, results);
          }
          return results;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Fallback cache périmé
        const stale = await getStaleEvaluations(cacheKey);
        if (stale && isMounted.current) {
          setEvaluations(stale);
          setSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          setError('Utilisation du cache périmé');
          return stale;
        }
        if (isMounted.current) {
          setError(err.message || 'Erreur de chargement');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [employeId]);

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchEvaluations();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, [fetchEvaluations]);

  // ============================================================
  // RAFRAÎCHISSEMENT MANUEL
  // ============================================================
  const handleRefresh = () => {
    if (!loading && !refreshing) {
      fetchEvaluations(true);
    }
  };

  // ============================================================
  // FONCTIONS CRUD (avec rechargement forcé après succès)
  // ============================================================
  const openCreateModal = () => {
    setEditingEvaluation(null);
    setFormData({
      evaluateur_id: '',
      date_evaluation: new Date().toISOString().slice(0, 10),
      commentaire: '',
      scores: {},
      note_globale: ''
    });
    setShowModal(true);
  };

  const openEditModal = (evalItem) => {
    setEditingEvaluation(evalItem);
    setFormData({
      evaluateur_id: evalItem.evaluateur_id,
      date_evaluation: evalItem.date_evaluation,
      commentaire: evalItem.commentaire || '',
      scores: evalItem.scores || {},
      note_globale: evalItem.note_globale || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = editingEvaluation ? 'update' : 'create';
    const payload = {
      ...formData,
      employe_id: employeId,
      note_globale: parseFloat(formData.note_globale) || null
    };
    if (editingEvaluation) payload.id = editingEvaluation.id;

    try {
      const res = await fetch(`${API_URL}?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setShowModal(false);
        // Forcer le rechargement après succès
        await fetchEvaluations(true);
        if (onEvaluationAdded) onEvaluationAdded();
      } else {
        setToast({ show: true, message: data.message, variant: 'danger' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Erreur serveur', variant: 'danger' });
    }
  };

  const handleDelete = async () => {
    const id = editingEvaluation?.id;
    if (!id) return;
    try {
      const formDataDelete = new FormData();
      formDataDelete.append('_method', 'DELETE');
      formDataDelete.append('id', id);
      const res = await fetch(`${API_URL}?action=delete`, {
        method: 'POST',
        body: formDataDelete
      });
      const data = await res.json();
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setShowDeleteModal(false);
        await fetchEvaluations(true);
        if (onEvaluationDeleted) onEvaluationDeleted();
      } else {
        setToast({ show: true, message: data.message, variant: 'danger' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Erreur serveur', variant: 'danger' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleScoreChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      scores: { ...prev.scores, [key]: value }
    }));
  };

  // ============================================================
  // RENDU AVEC STYLES RESPONSIFS
  // ============================================================
  return (
    <>
      <style>{`
        .evaluation-widget {
          background: rgba(255,255,255,0.03);
          border-radius: 24px;
          padding: clamp(0.8rem, 1.5vw, 1.5rem);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .evaluation-widget .widget-header {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 1.5rem;
        }
        .evaluation-widget .widget-title {
          font-size: clamp(1rem, 1.5vw, 1.2rem);
          font-weight: 600;
          color: #e0e7ff;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .widget-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .badge-source {
          font-size: clamp(0.55rem, 0.8vw, 0.7rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue, #3b82f6);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }
        .btn-sm-glass {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: #e0e7ff;
          padding: 6px 14px;
          border-radius: 30px;
          font-size: clamp(0.7rem, 0.9vw, 0.85rem);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: 0.2s;
          cursor: pointer;
          white-space: nowrap;
          min-height: 40px;
          touch-action: manipulation;
        }
        .btn-sm-glass:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
        }
        .btn-sm-glass.primary {
          border-color: #3b82f6;
          color: #3b82f6;
        }
        .btn-sm-glass.primary:hover:not(:disabled) {
          background: rgba(59, 130, 246, 0.12);
        }
        .btn-sm-glass:disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        .evaluation-card {
          background: rgba(255,255,255,0.04);
          border-radius: 16px;
          padding: clamp(0.8rem, 1.2vw, 1rem) clamp(0.8rem, 1.2vw, 1.2rem);
          margin-bottom: 1rem;
          border: 1px solid rgba(255,255,255,0.04);
          transition: 0.2s;
        }
        .evaluation-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.08);
        }
        .evaluation-card .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 8px;
        }
        .evaluation-card .card-header .evaluateur {
          font-weight: 500;
          color: #e0e7ff;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
        }
        .evaluation-card .card-header .date {
          font-size: clamp(0.7rem, 0.9vw, 0.8rem);
          color: #94a3b8;
        }
        .evaluation-card .note-globale {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 8px 0;
          flex-wrap: wrap;
        }
        .evaluation-card .note-globale .note-value {
          font-weight: 700;
          font-size: clamp(1.2rem, 2vw, 1.4rem);
          color: #fbbf24;
        }
        .evaluation-card .commentaire {
          color: #94a3b8;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
          margin: 8px 0;
        }
        .evaluation-card .card-actions {
          display: flex;
          gap: 6px;
          margin-top: 8px;
        }
        .evaluation-card .card-actions button {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 20px;
          transition: 0.2s;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
        }
        .evaluation-card .card-actions button:hover {
          background: rgba(255,255,255,0.06);
          color: #e0e7ff;
        }
        .evaluation-card .card-actions button.danger:hover {
          color: #ef4444;
        }
        .score-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin: 8px 0;
        }
        .score-tag {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.15);
          border-radius: 20px;
          padding: 2px 12px;
          font-size: clamp(0.65rem, 0.8vw, 0.75rem);
          color: #94a3b8;
        }
        .empty-state {
          color: #94a3b8;
          text-align: center;
          padding: 2rem 0;
          font-size: clamp(0.9rem, 1.2vw, 1rem);
        }

        /* Modales */
        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: #e0e7ff;
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(0.8rem, 1.5vw, 1.2rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-modal .modal-body {
          padding: clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: #e0e7ff;
          padding: 10px 14px;
          width: 100%;
          transition: 0.3s;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
        }
        .monetra-input:focus {
          border-color: #3b82f6;
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: #64748b;
        }
        .monetra-input option {
          background: #0b1428;
          color: #e0e7ff;
        }
        .score-input-group {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }
        .score-input-group label {
          flex: 0 0 120px;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
          color: #94a3b8;
        }
        .score-input-group input {
          flex: 1;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 6px 12px;
          color: #e0e7ff;
          width: 80px;
          min-width: 60px;
        }
        .score-input-group input:focus {
          border-color: #3b82f6;
          outline: none;
        }

        .toast-eval {
          position: fixed;
          top: clamp(12px, 2vh, 20px);
          right: clamp(12px, 2vw, 20px);
          z-index: 9999;
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          color: #e0e7ff;
          padding: 1rem 1.2rem;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6);
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: clamp(200px, 30vw, 300px);
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-eval .close-toast {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.2rem;
          cursor: pointer;
        }

        .monetra-btn.outline-secondary {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.06);
          color: #94a3b8;
        }
        .monetra-btn.outline-secondary:hover {
          background: rgba(255,255,255,0.04);
        }
        .monetra-btn.primary {
          background: #3b82f6;
          border: none;
          color: #fff;
        }
        .monetra-btn.primary:hover {
          background: #2563eb;
        }
        .monetra-btn.danger {
          background: rgba(239,68,68,0.12);
          color: #f87171;
          border: 1px solid rgba(239,68,68,0.2);
        }
        .monetra-btn.danger:hover {
          background: rgba(239,68,68,0.25);
        }
        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          font-weight: 500;
          transition: 0.2s;
          cursor: pointer;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
          min-height: 40px;
        }

        @media (max-width: 768px) {
          .evaluation-widget .widget-header {
            flex-direction: column;
            align-items: stretch;
          }
          .widget-actions {
            flex-direction: column;
            align-items: stretch;
          }
          .btn-sm-glass {
            justify-content: center;
          }
          .score-input-group {
            flex-direction: column;
            align-items: stretch;
          }
          .score-input-group label {
            flex: none;
          }
          .score-input-group input {
            width: 100%;
          }
          .evaluation-card .card-header {
            flex-direction: column;
            align-items: stretch;
          }
          .evaluation-card .card-actions {
            justify-content: flex-end;
          }
        }
        @media (max-width: 480px) {
          .btn-sm-glass {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      <div className="evaluation-widget">
        <div className="widget-header">
          <div className="widget-title">
            <FontAwesomeIcon icon={faStar} style={{ color: '#fbbf24' }} />
            Évaluations
          </div>
          <div className="widget-actions">
            <span className={`badge-source ${source === 'live' ? 'live' : source === 'cache (périmé)' ? 'warning' : ''}`}>
              {source === 'cache' ? '📦 Cache' :
               source === 'cache (périmé)' ? '⚠️ Cache périmé' :
               source === 'cache (partagé)' ? '🔄 Partage' :
               source === 'live' ? '🔄 Live' :
               source === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="btn-sm-glass" onClick={handleRefresh} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
           
          </div>
        </div>

        {loading && !refreshing ? (
          <div className="empty-state">Chargement...</div>
        ) : error && evaluations.length === 0 ? (
          <div className="empty-state" style={{ color: '#f87171' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </div>
        ) : evaluations.length === 0 ? (
          <div className="empty-state">Aucune évaluation pour cet employé.</div>
        ) : (
          evaluations.map(evalItem => {
            const noteGlobale = parseFloat(evalItem.note_globale);
            const noteDisplay = !isNaN(noteGlobale) ? noteGlobale.toFixed(1) : '—';
            return (
              <div className="evaluation-card" key={evalItem.id}>
                <div className="card-header">
                  <div>
                    <span className="evaluateur">
                      <FontAwesomeIcon icon={faUser} className="me-1" />
                      {evalItem.evaluateur_nom || 'Évaluateur'}
                    </span>
                    <span className="date ms-3">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {evalItem.date_evaluation}
                    </span>
                  </div>
                  <div className="card-actions">
                   
                    <button className="danger" onClick={() => { setEditingEvaluation(evalItem); setShowDeleteModal(true); }}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
                <div className="note-globale">
                  <span className="note-value">{noteDisplay}</span>
                  <span>{renderStars(noteGlobale)}</span>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>/ 5</span>
                </div>
                {evalItem.scores && Object.keys(evalItem.scores).length > 0 && (
                  <div className="score-tags">
                    {Object.entries(evalItem.scores).map(([key, val]) => (
                      <span key={key} className="score-tag">{key}: {val}/5</span>
                    ))}
                  </div>
                )}
                {evalItem.commentaire && (
                  <div className="commentaire">
                    <FontAwesomeIcon icon={faComment} className="me-1" />
                    {evalItem.commentaire}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* TOAST */}
      {toast.show && (
        <div className="toast-eval">
          <FontAwesomeIcon
            icon={toast.variant === 'success' ? faCheck : faExclamationTriangle}
            style={{ color: toast.variant === 'success' ? '#3b82f6' : '#ef4444' }}
          />
          <span>{toast.message}</span>
          <button className="close-toast" onClick={() => setToast({ ...toast, show: false })}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* MODALE AJOUT / ÉDITION */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal" size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faStar} className="text-warning me-2" />
              {editingEvaluation ? 'Modifier' : 'Ajouter'} une évaluation
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="text-secondary small">Évaluateur</label>
                <select
                  name="evaluateur_id"
                  value={formData.evaluateur_id}
                  onChange={handleChange}
                  className="monetra-input"
                  required
                >
                  <option value="">Sélectionner un évaluateur</option>
                  <option value="1">SA</option>
                  <option value="2">DSI</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Date de l'évaluation</label>
                <input
                  type="date"
                  name="date_evaluation"
                  value={formData.date_evaluation}
                  onChange={handleChange}
                  className="monetra-input"
                  required
                />
              </div>
              <div className="col-12">
                <label className="text-secondary small">Note globale (/5)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  name="note_globale"
                  value={formData.note_globale}
                  onChange={handleChange}
                  className="monetra-input"
                />
              </div>
              <div className="col-12">
                <label className="text-secondary small">Commentaire</label>
                <textarea
                  name="commentaire"
                  value={formData.commentaire}
                  onChange={handleChange}
                  rows="3"
                  className="monetra-input"
                  placeholder="Commentaires sur l'évaluation..."
                />
              </div>
              <div className="col-12">
                <label className="text-secondary small">Scores par critère (optionnel)</label>
                <div className="score-input-group">
                  <label>Compétence technique</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.scores?.technique || ''}
                    onChange={(e) => handleScoreChange('technique', parseFloat(e.target.value))}
                  />
                </div>
                <div className="score-input-group">
                  <label>Communication</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.scores?.communication || ''}
                    onChange={(e) => handleScoreChange('communication', parseFloat(e.target.value))}
                  />
                </div>
                <div className="score-input-group">
                  <label>Leadership</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.scores?.leadership || ''}
                    onChange={(e) => handleScoreChange('leadership', parseFloat(e.target.value))}
                  />
                </div>
                <div className="score-input-group">
                  <label>Travail d'équipe</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.scores?.teamwork || ''}
                    onChange={(e) => handleScoreChange('teamwork', parseFloat(e.target.value))}
                  />
                </div>
                <p className="text-secondary small mt-2">Vous pouvez ajouter d'autres critères en modifiant le code.</p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn outline-secondary">
              Annuler
            </Button>
            <Button variant="success" type="submit" className="monetra-btn primary">
              Enregistrer
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE SUPPRESSION */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger"><FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer cette évaluation ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn outline-secondary">
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger">
            Confirmer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ============================================================
// UTILITAIRE DE RENDU DES ÉTOILES
// ============================================================
const renderStars = (note) => {
  const num = parseFloat(note);
  if (isNaN(num) || num <= 0) return null;
  const full = Math.floor(num);
  const half = num - full >= 0.5 ? 1 : 0;
  const stars = [];
  for (let i = 0; i < full; i++) {
    stars.push(<FontAwesomeIcon key={i} icon={faStar} className="text-warning" />);
  }
  if (half) {
    stars.push(<FontAwesomeIcon key="half" icon={faStarHalfAlt} className="text-warning" />);
  }
  const empty = 5 - full - half;
  for (let i = 0; i < empty; i++) {
    stars.push(<FontAwesomeIcon key={`empty-${i}`} icon={faStar} className="text-secondary" style={{ opacity: 0.3 }} />);
  }
  return stars;
};

export default EmployeeEvaluationWidget;